import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fab,
  List,
  Slide,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';

import SearchQueryContext from 'contexts/SearchQueryContext';
import { useMessageGroups } from 'hooks/useMessageGroups';
import { useMessageScroll } from 'hooks/useMessageScroll';
import { useVisibilityDateMessage } from 'hooks/useVisibilityDateMessage';
import { useAppDispatch, useAppSelector } from 'store';
import {
  MensagemInterface,
  mensagensSelector,
  setMensagemEstaMontada,
  TiposMensagemEnum,
} from 'store/reducers/mensagens';

import { EventGroupMessage } from './EventGroupMessage';
import MessageItem from './MessageItem';
import VirtualizedListContainer from './VirtualizedListContainer';

type MessagesContainerProps = {
  messages: MensagemInterface[];
  next: () => void;
  previous?: () => void;
  hasAfterCursor: boolean;
  hasBeforeCursor?: boolean;
  inverse?: boolean;
  loading?: boolean;
  endMessage?: string;
  scrollableTargetId?: string;
  isGroup?: boolean;
  actions?: ReactNode[] | false;
  countMensagensNaoLidas?: number;
  verMensagemCitadaNoModal?: boolean;
  historicoAtendimento?: boolean;
  onScrollToTop?: (force?: boolean) => void;
  onResetLista?: () => void;
  onReenviarMensagem?: (mensagem: MensagemInterface) => void;
  onApagarMensagem?: (mensagemId: string) => void;
  onResponderMensagem?: (mensagem: MensagemInterface) => void;
  onEditarMensagem?: (mensagem: MensagemInterface) => void;
  onVerHistoricoMensagemEditada?: (mensagemId: string) => void;
  searchQuery?: string;
};

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
  },
  appBar: {
    alignItems: 'center',
    '& > .MuiButton-root': {
      minWidth: '444px',
    },
  },
  scrollContainer: {
    height: '100%',
    width: '100%',
    overflow: 'auto',
    position: 'relative',
  },
  fab: {
    color: palette.getContrastText(palette.background.tertiary),
    backgroundColor: `${palette.background.tertiary} !important`,
  },
  listInfoContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: spacing(2),
  },
  chip: {
    fontWeight: 500,
    borderRadius: spacing(1),
    backgroundColor: palette.common.white,
    color: palette.getContrastText(palette.common.white),
    marginTop: spacing(1),
  },
}));

export default function NewMessagesContainer(props: MessagesContainerProps) {
  const {
    messages,
    next,
    previous,
    hasAfterCursor,
    hasBeforeCursor,
    inverse = true,
    loading,
    endMessage,
    scrollableTargetId = 'listMessagesContainer',
    countMensagensNaoLidas = 0,
    onScrollToTop,
    onResetLista,
    searchQuery = '',
    ...restProps
  } = props;
  const classes = useStyles();
  const { mensagemIdPesquisada } = useAppSelector(mensagensSelector);
  const dispatch = useAppDispatch();

  const [disablePagination, setDisablePagination] = useState(false);
  const { virtualizedItems } = useMessageGroups(messages);

  const rowVirtualizer = useVirtualizer({
    count: virtualizedItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const item = virtualizedItems[index];

      if (item?.type === 'group') {
        return 50;
      }

      const msg = item?.data as MensagemInterface;
      if (!msg) return 100;

      // Mensagens com citação (reply) ocupam mais espaço vertical
      const replyOffset = msg.mensagemCitada ? 80 : 0;

      switch (msg.tipo) {
        case TiposMensagemEnum.TEXTO:
          return (
            72 + Math.ceil((msg.mensagem?.length || 0) / 40) * 22 + replyOffset
          );
        case TiposMensagemEnum.IMAGEM:
        case TiposMensagemEnum.VIDEO:
        case TiposMensagemEnum.LOCALIZACAO:
          return 320 + replyOffset;
        case TiposMensagemEnum.AUDIO:
          return 90 + replyOffset;
        case TiposMensagemEnum.DOCUMENTO:
          return 100 + replyOffset;
        case TiposMensagemEnum.TEMPLATE_WHATSAPP:
        case TiposMensagemEnum.WPP_CTA:
        case TiposMensagemEnum.WPP_REPLY_BTN:
          return 220 + replyOffset;
        case TiposMensagemEnum.WPP_LIST:
        case TiposMensagemEnum.WPP_LIST_RESP:
          return 180 + replyOffset;
        default:
          return 140 + replyOffset;
      }
    },
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const isMarkedAsUnread = countMensagensNaoLidas === -1;

  const { parentRef, isTop, handleScroll } = useMessageScroll({
    rowVirtualizer,
    itemsLength: virtualizedItems.length,
    next,
    previous,
    hasAfterCursor,
    hasBeforeCursor,
    loading,
    inverse,
    onScrollToTop,
    onResetLista,
    disablePagination,
    mensagemIdPesquisada,
    isMarkedAsUnread,
  });

  const { dataVisivel, visible } = useVisibilityDateMessage({
    rowVirtualizer,
    items: virtualizedItems,
  });

  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const lastScrolledMessageRef = useRef<string | null>(null);

  const scrollToMessage = useCallback(
    (virtualIndex: number, messageId: string) => {
      const scrollWithRetry = (attempts = 0) => {
        const maxAttempts = 3;

        if (
          !parentRef.current ||
          virtualIndex < 0 ||
          virtualIndex >= virtualizedItems.length
        ) {
          setDisablePagination(false);
          return;
        }

        requestAnimationFrame(() => {
          try {
            rowVirtualizer.scrollToIndex(virtualIndex, {
              align: 'center',
              behavior: 'auto',
            });
          } catch (error) {
            setDisablePagination(false);
            return;
          }

          const checkScrollSuccess = () => {
            scrollTimeoutRef.current = setTimeout(
              () => {
                if (!parentRef.current) return;

                const currentVirtualItems = rowVirtualizer.getVirtualItems();
                const targetFound = currentVirtualItems.some(item => {
                  const virtualItem = virtualizedItems[item.index];
                  return virtualItem?.messageIds.includes(messageId);
                });

                if (!targetFound && attempts < maxAttempts) {
                  scrollWithRetry(attempts + 1);
                } else {
                  lastScrolledMessageRef.current = messageId;
                  setDisablePagination(false);
                }
              },
              attempts === 0 ? 400 : 300,
            );
          };

          checkScrollSuccess();
        });
      };

      scrollWithRetry();
    },
    [rowVirtualizer, virtualizedItems, parentRef],
  );

  useEffect(() => {
    if (
      mensagemIdPesquisada &&
      lastScrolledMessageRef.current !== mensagemIdPesquisada
    ) {
      const virtualIndex = virtualizedItems.findIndex(item =>
        item.messageIds.includes(mensagemIdPesquisada),
      );

      if (virtualIndex !== -1) {
        dispatch(setMensagemEstaMontada(true));

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        setDisablePagination(true);

        scrollTimeoutRef.current = setTimeout(() => {
          scrollToMessage(virtualIndex, mensagemIdPesquisada);
        }, 100);
      } else {
        dispatch(setMensagemEstaMontada(false));
      }
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [mensagemIdPesquisada, virtualizedItems, dispatch, scrollToMessage]);

  useEffect(() => {
    lastScrolledMessageRef.current = null;
  }, [mensagemIdPesquisada]);

  const renderVirtualItem = useCallback(
    (virtualRow: VirtualItem) => {
      const item = virtualizedItems[virtualRow.index];
      if (!item) return null;

      return (
        <div
          key={virtualRow.key}
          data-index={virtualRow.index}
          ref={rowVirtualizer.measureElement}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualRow.start}px)`,
          }}
        >
          {item.type === 'group' ? (
            <EventGroupMessage eventos={item.data as MensagemInterface[]} />
          ) : (
            <MessageItem
              mensagem={item.data as MensagemInterface}
              {...restProps}
            />
          )}
        </div>
      );
    },
    [virtualizedItems, rowVirtualizer.measureElement, restProps],
  );

  return (
    <SearchQueryContext.Provider value={searchQuery}>
      <Box className={classes.root}>
        {dataVisivel && (
          <Slide direction="down" in={visible} mountOnEnter unmountOnExit>
            <AppBar
              className={classes.appBar}
              position="absolute"
              color="transparent"
              elevation={0}
            >
              <Chip size="small" className={classes.chip} label={dataVisivel} />
            </AppBar>
          </Slide>
        )}
        {!isTop && countMensagensNaoLidas > 0 && (
          <AppBar
            className={classes.appBar}
            position="absolute"
            color="transparent"
            elevation={0}
          >
            <Button color="primary" variant="contained" onClick={handleScroll}>
              <Typography variant="body2" color="inherit">
                {`Você tem ${countMensagensNaoLidas} ${
                  countMensagensNaoLidas > 1
                    ? 'mensagens não lidas'
                    : 'mensagem não lida'
                }`}
              </Typography>

              <Box flexGrow={1} />

              <Typography variant="inherit">Visualizar</Typography>
            </Button>
          </AppBar>
        )}

        <List
          ref={parentRef}
          className={classes.scrollContainer}
          id={scrollableTargetId}
        >
          {loading && (
            <Box className={classes.listInfoContainer}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!hasAfterCursor && endMessage && (
            <Box className={classes.listInfoContainer}>
              <Typography variant="caption" color="textSecondary">
                {endMessage}
              </Typography>
            </Box>
          )}

          <VirtualizedListContainer
            virtualizer={rowVirtualizer}
            virtualItems={virtualItems}
            positionItems={false}
          >
            {virtualItems.map(renderVirtualItem)}
          </VirtualizedListContainer>
        </List>

        <Slide direction="up" in={!isTop} mountOnEnter unmountOnExit>
          <Box position="absolute" right={16} bottom={16}>
            <Fab size="medium" onClick={handleScroll} className={classes.fab}>
              {inverse ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
            </Fab>
          </Box>
        </Slide>
      </Box>
    </SearchQueryContext.Provider>
  );
}
